// /app/frontend/src/components/admin/DebugSnapshotButton.jsx
/**
 * Debug Snapshot Button (Admin-only)
 * Copies Rule 0 debug data to clipboard for rapid issue diagnosis.
 */
import React, { useContext, useState } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { useEntitlements } from '../../context/EntitlementsContext';
import { getDebugSnapshot, copyDebugSnapshot } from '../../utils/debugSnapshot';

export default function DebugSnapshotButton({ className = '' }) {
  const { user } = useContext(AuthContext);
  const entitlements = useEntitlements();
  const [copied, setCopied] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackJson, setFallbackJson] = useState('');

  const handleCopy = async () => {
    // Generate snapshot
    const snapshot = getDebugSnapshot({
      user,
      entitlements,
      buildTimestamp: process.env.REACT_APP_BUILD_TIMESTAMP,
      buildCommit: process.env.REACT_APP_BUILD_COMMIT,
    });

    // Try clipboard
    const result = await copyDebugSnapshot(snapshot);

    if (result.success) {
      setCopied(true);
      toast.success('Debug snapshot copied to clipboard!', {
        description: 'Paste into support ticket or notes',
        duration: 3000,
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Fallback: show modal with textarea
      setFallbackJson(result.json);
      setShowFallback(true);
      toast.error('Clipboard blocked - use manual copy', {
        duration: 3000,
      });
    }
  };

  const handleSelectAll = (e) => {
    e.target.select();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors
          ${copied 
            ? 'bg-green-600 text-white' 
            : 'bg-slate-700 text-white hover:bg-slate-600 border border-slate-600'
          } ${className}`}
        data-testid="debug-snapshot-btn"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            <span>Copied!</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            <span>Copy Debug Snapshot</span>
          </>
        )}
      </button>

      {/* Fallback Modal */}
      {showFallback && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Clipboard Blocked</span>
              </div>
              <button
                type="button"
                onClick={() => setShowFallback(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Instructions */}
            <div className="px-4 py-2 text-sm text-gray-300">
              Select all (Ctrl/Cmd+A) and copy (Ctrl/Cmd+C):
            </div>

            {/* Textarea */}
            <div className="flex-1 p-4 pt-0 overflow-hidden">
              <textarea
                readOnly
                value={fallbackJson}
                onClick={handleSelectAll}
                className="w-full h-full min-h-[300px] p-3 bg-slate-900 border border-slate-600 rounded-lg text-xs text-gray-300 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700">
              <button
                type="button"
                onClick={() => setShowFallback(false)}
                className="w-full py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
